import { fetchAdminUsers } from '@/api/admin';
import { Spinner } from '@/components/ui/spinner';
import type { IAdminUser, UserRole } from '@/entities/admin-user.entity';
import { useVirtualizer } from '@tanstack/react-virtual';
import { LoaderCircleIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import UserCard from './user-card';

const LIMIT = 30;

interface UsersListProps {
	search: string;
	role: UserRole | 'all';
	onTotalChange?: (total: number) => void;
}

export default function UsersList({ search, role, onTotalChange }: UsersListProps) {
	const listRef = useRef<HTMLDivElement | null>(null);
	const [users, setUsers] = useState<IAdminUser[]>([]);
	const [nextCursor, setNextCursor] = useState<number | null>(null);
	const [hasMore, setHasMore] = useState(true);
	const [isFetching, setIsFetching] = useState(false);
	const [firstLoaded, setFirstLoaded] = useState(false);
	const fetchingRef = useRef(false);

	const fetchKey = `${search}:${role}`;

	useEffect(() => {
		let cancelled = false;

		async function loadInitial() {
			fetchingRef.current = true;
			setIsFetching(true);
			setFirstLoaded(false);
			try {
				const { users: newUsers, nextCursor: cursor } = await fetchAdminUsers({
					search: search || undefined,
					role: role === 'all' ? undefined : role,
					limit: LIMIT,
				});
				if (cancelled) return;
				setUsers(newUsers);
				setNextCursor(cursor);
				setHasMore(cursor !== null);
			} finally {
				if (!cancelled) {
					setIsFetching(false);
					setFirstLoaded(true);
					fetchingRef.current = false;
				}
			}
		}

		loadInitial();
		return () => {
			cancelled = true;
		};
	}, [fetchKey, search, role]);

	useEffect(() => {
		onTotalChange?.(users.length);
	}, [users.length, onTotalChange]);

	async function loadMore() {
		if (fetchingRef.current || !hasMore || nextCursor === null) return;
		fetchingRef.current = true;
		setIsFetching(true);
		try {
			const { users: newUsers, nextCursor: cursor } = await fetchAdminUsers({
				search: search || undefined,
				role: role === 'all' ? undefined : role,
				cursor: nextCursor,
				limit: LIMIT,
			});
			setUsers(prev => [...prev, ...newUsers]);
			setNextCursor(cursor);
			setHasMore(cursor !== null);
		} finally {
			setIsFetching(false);
			fetchingRef.current = false;
		}
	}

	const virtualizer = useVirtualizer({
		count: users.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 100,
		overscan: 5,
		measureElement: el => el.getBoundingClientRect().height,
	});

	const virtualItems = virtualizer.getVirtualItems();

	useEffect(() => {
		const last = virtualItems.at(-1);
		if (!last) return;
		if (last.index >= users.length - 5) {
			loadMore();
		}
	}, [virtualItems, users.length]);

	if (!firstLoaded) {
		return (
			<div className='h-full flex items-center justify-center'>
				<Spinner />
			</div>
		);
	}

	if (users.length === 0) {
		return (
			<div className='h-full flex items-center justify-center text-muted-foreground text-sm'>
				Пользователи не найдены
			</div>
		);
	}

	return (
		<div ref={listRef} className='h-full overflow-auto' data-scroll-container>
			<div
				style={{
					height: `${virtualizer.getTotalSize()}px`,
					width: '100%',
					position: 'relative',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
					}}
				>
					{virtualItems.map(({ index, key }) => (
						<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
							<UserCard user={users[index]!} />
						</div>
					))}
				</div>
			</div>
			{isFetching && (
				<div className='flex justify-center py-3'>
					<LoaderCircleIcon className='animate-spin text-muted-foreground' size={20} />
				</div>
			)}
		</div>
	);
}
